// Shared field metadata for the custom form builder. The 10 question types and
// photo-requirement values mirror the backend enums on CustomFormQuestion; the
// wire values (`value`) must stay in sync with those enum keys.

export interface QuestionOption {
  token: string;
  label: string;
}

export interface BuilderQuestion {
  _key: string;
  id: number | null;
  title: string;
  description: string;
  question_type: string;
  unit: string | null;
  required: boolean;
  photo_requirement: string;
  photo_min_count: number;
  customer_visible: boolean;
  options: QuestionOption[];
}

export interface BuilderSection {
  _key: string;
  id: number | null;
  name: string;
  description: string;
  questions: BuilderQuestion[];
  removedQuestionIds: number[];
}

export interface QuestionType {
  value: string;
  label: string;
  icon: string;
  color: string;
  description: string;
}

export const QUESTION_TYPES: QuestionType[] = [
  {
    value: "status_ryg",
    label: "Status (Red / Yellow / Green)",
    icon: "fa-traffic-light",
    color: "text-amber-500",
    description: "Red / Yellow / Green rating for quick condition checks."
  },
  {
    value: "yes_no_na",
    label: "Yes / No / N/A",
    icon: "fa-circle-check",
    color: "text-emerald-500",
    description: "A Yes, No, or N/A choice."
  },
  {
    value: "single_select",
    label: "Single select",
    icon: "fa-square-caret-down",
    color: "text-sky-500",
    description: "Choose one option from a dropdown."
  },
  {
    value: "multi_select",
    label: "Multi select",
    icon: "fa-list-check",
    color: "text-indigo-500",
    description: "Choose one or more options."
  },
  {
    value: "number",
    label: "Number",
    icon: "fa-hashtag",
    color: "text-cyan-600",
    description: "A numeric entry (e.g. a reading), with an optional unit."
  },
  {
    value: "text",
    label: "Text",
    icon: "fa-font",
    color: "text-slate-500",
    description: "A free-text written answer."
  },
  {
    value: "date",
    label: "Date",
    icon: "fa-calendar-days",
    color: "text-rose-500",
    description: "A single calendar date the tech picks."
  },
  {
    value: "checkbox",
    label: "Checkbox",
    icon: "fa-square-check",
    color: "text-teal-500",
    description: "A single checkbox to confirm it's done."
  },
  {
    value: "star_rating",
    label: "Star rating",
    icon: "fa-star",
    color: "text-yellow-500",
    description: "A 1–5 star rating."
  },
  {
    value: "signature",
    label: "Signature",
    icon: "fa-signature",
    color: "text-fuchsia-500",
    description: "Capture a signature on-screen."
  },
  {
    value: "photo_only",
    label: "Photo",
    icon: "fa-camera",
    color: "text-pink-500",
    description: "One or more photos are the answer — no other input."
  }
];

const TEXT_TYPE: QuestionType = QUESTION_TYPES[5];

export function questionType(value: string): QuestionType {
  return QUESTION_TYPES.find((t) => t.value === value) ?? TEXT_TYPE;
}

export const PHOTO_REQUIREMENTS: { value: string; label: string }[] = [
  { value: "no_photos", label: "No photos" },
  { value: "optional_photos", label: "Optional photos" },
  { value: "required_photos", label: "Required photos" }
];

// Types whose answers come from a configurable option pool (editable
// token/label pairs). status_ryg and yes_no_na are deliberately absent: their
// tokens are system-owned, and both the builder preview and the tech app render
// them as fixed controls, so an editable pool here would configure a field
// nothing reads.
const OPTION_TYPES = ["single_select", "multi_select"];

// Display labels for the system-owned choice types, keyed by token. These two
// draw from a fixed vocabulary rather than the author's `options` pool, so a
// stored option list cannot supply their labels — the read-only answer view
// would otherwise print the raw token.
//
// The tokens are not guesses: they match the tech app's controls
// (ui-kit YesNoNa.tsx `"yes" | "no" | "na"`, StatusRyg.tsx
// `"red" | "yellow" | "green"`) and TC-417's server-side SYSTEM_TOKENS, which
// owns the vocabulary and makes both types system-owned. Once that lands, the
// labels here are the only client-side piece left; serving them from the
// contract instead would be a change to discuss with that ticket rather than
// something to fork here.
export const SYSTEM_TOKEN_LABELS: Record<string, Record<string, string>> = {
  yes_no_na: { yes: "Yes", no: "No", na: "N/A" },
  status_ryg: { red: "Red", yellow: "Yellow", green: "Green" }
};

export function typeHasOptions(questionType: string): boolean {
  return OPTION_TYPES.includes(questionType);
}

export function typeHasUnit(questionType: string): boolean {
  return questionType === "number";
}

// A photo-only question has no scalar answer control — photos are the answer —
// so its editor hides options/unit and the photo-requirement dropdown, and it
// always carries a minimum photo count.
export function isPhotoOnly(questionType: string): boolean {
  return questionType === "photo_only";
}

export function photosRequired(photoRequirement: string): boolean {
  return photoRequirement === "required_photos";
}

// Row action icon button (duplicate / move / remove). Neutral by default; each
// button layers its own hover colour (e.g. red for remove) on top.
export const CF_ACTION_BTN =
  "cf-action rounded p-1 text-gray-400 transition-colors hover:text-gray-600 " +
  "focus:outline-none " +
  "disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-gray-400";

// Machine-safe key derived from an option's display text: lowercase, runs of
// non-alphanumerics collapsed to underscores, trimmed. Empty text yields an
// empty token (the row reads as incomplete rather than inventing a key); it is
// uniqueToken's job to make sure two of those never collide.
export function slugifyToken(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Keep tokens unique within a question by suffixing collisions (good, good_2).
// A blank base still gets a suffix: slugifyToken returns "" for any label with no
// [a-z0-9] in it at all (CJK, punctuation-only), so two such labels would
// otherwise both store the token "" — and an answer holding "" resolves to
// whichever option comes first, rendering the wrong label to the office and to
// the customer with nothing to indicate it.
export function uniqueToken(base: string, taken: string[]): string {
  if (base && !taken.includes(base)) return base;

  const stem = base || "option";
  let suffix = base ? 2 : 1;
  while (taken.includes(`${stem}_${suffix}`)) suffix += 1;
  return `${stem}_${suffix}`;
}

// Options are edited as a comma- or newline-separated list; each line becomes an
// option with an auto-derived, question-unique token.
export function parseOptionList(text: string): QuestionOption[] {
  const taken: string[] = [];
  return text
    .split(/[\n,]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((label) => {
      const token = uniqueToken(slugifyToken(label), taken);
      taken.push(token);
      return { token, label };
    });
}

export function optionsToText(options: QuestionOption[]): string {
  return options.map((option) => option.label).join("\n");
}

// Stable client-only keys for v-for/draggable identity. New nodes have no
// server id yet, so we can't key on it; this counter gives every node a unique
// key for its lifetime in the builder.
let keyCounter = 0;
function nextKey(prefix = "k"): string {
  keyCounter += 1;
  return `${prefix}-${keyCounter}`;
}

export function newQuestion(questionType = "text"): BuilderQuestion {
  const photoOnly = isPhotoOnly(questionType);
  return {
    _key: nextKey("q"),
    id: null,
    title: "",
    description: "",
    question_type: questionType,
    unit: null,
    required: true,
    // Photos are the answer for photo-only, so it defaults to required photos
    // with a minimum of 1; the Required toggle governs whether it gates closing.
    photo_requirement: photoOnly ? "required_photos" : "no_photos",
    photo_min_count: photoOnly ? 1 : 0,
    customer_visible: true,
    options: []
  };
}

export function newSection(): BuilderSection {
  return {
    _key: nextKey("s"),
    id: null,
    name: "",
    description: "",
    questions: [],
    removedQuestionIds: []
  };
}

// Deep copies for the duplicate actions. Clones get fresh client keys and null
// ids so they persist as brand-new records rather than aliasing the original.
export function cloneQuestion(question: BuilderQuestion): BuilderQuestion {
  return {
    ...question,
    _key: nextKey("q"),
    id: null,
    options: question.options.map((option) => ({ ...option }))
  };
}

export function cloneSection(section: BuilderSection): BuilderSection {
  return {
    ...section,
    _key: nextKey("s"),
    id: null,
    removedQuestionIds: [],
    questions: section.questions.map(cloneQuestion)
  };
}
